import React, { useEffect, useState } from 'react';
import { Loader2, Save, CheckCircle2, AlertTriangle, TrendingUp, ClipboardList } from 'lucide-react';

/**
 * WorkCompletionTab — records the ACTUAL materials consumed on this
 * appraisal once the job is finished. Shown side-by-side with the
 * estimated qty pulled from the chosen reroof spec's calc snapshot so
 * the office sees the over/under at a glance.
 *
 * Saved actuals feed the /api/work-completion/ratios aggregate which
 * surfaces a rolling estimate-vs-actual factor per material — used as
 * a SUGGESTION the office can apply to rate-card defaults (never auto-
 * applied, per the spec).
 */
export default function WorkCompletionTab({ api, appraisalId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [flash, setFlash]     = useState(null);
  const [materials, setMaterials] = useState([]);
  const [estimate, setEstimate]   = useState({});
  const [actuals, setActuals]     = useState({});
  const [completionNotes, setCompletionNotes] = useState('');
  const [completedAt, setCompletedAt] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/appraisals/${appraisalId}/work-completion`).then((r) => {
      if (!alive) return;
      const d = r.data || {};
      setMaterials(d.materials || []);
      setEstimate(d.estimate || {});
      setActuals(d.actuals || {});
      setCompletionNotes(d.completion_notes || '');
      setCompletedAt((d.completed_at || '').substring(0, 10));
    }).catch((e) => {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [api, appraisalId]);

  const setActual = (key, patch) => {
    setActuals((a) => ({ ...a, [key]: { ...(a[key] || {}), ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    setFlash(null);
    try {
      const r = await api.put(`/appraisals/${appraisalId}/work-completion`, {
        actuals,
        completion_notes: completionNotes,
        completed_at: completedAt ? new Date(completedAt).toISOString() : new Date().toISOString(),
      });
      setActuals(r.data?.actuals || actuals);
      setFlash({ tone: 'ok', msg: 'Work completion record saved ✓' });
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-500"><Loader2 size={14} className="inline animate-spin mr-1" /> Loading work completion…</div>;
  }

  const variance = (key) => {
    const est = Number(estimate[key] || 0);
    const act = Number((actuals[key] || {}).qty || 0);
    if (est === 0) return null;
    return ((act - est) / est) * 100;
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="work-completion-tab">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <ClipboardList size={18} className="text-emerald-600" /> Work Completion · actual materials used
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Capture the exact quantities consumed on this job. The system compares each value to the chosen quote's estimate and rolls them into a "suggested rate-card adjustment" you can apply when you're ready.
          </p>
        </div>
        <button
          data-testid="work-completion-save"
          onClick={save}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-md px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save actuals
        </button>
      </div>

      {flash && (
        <div data-testid="work-completion-flash" className={`text-sm rounded-md p-3 border ${flash.tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {flash.tone === 'ok' ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
          {flash.msg}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left p-2 px-3 w-1/4">Material</th>
              <th className="text-right p-2 px-3">Estimated</th>
              <th className="text-right p-2 px-3 w-32">Actual used</th>
              <th className="text-right p-2 px-3">Variance</th>
              <th className="text-left p-2 px-3">Notes</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((m) => {
              const est = Number(estimate[m.key] || 0);
              const act = Number((actuals[m.key] || {}).qty || 0);
              const v = variance(m.key);
              const variancePillCls =
                v === null ? 'text-slate-400' :
                Math.abs(v) < 5 ? 'text-emerald-700' :
                v > 0 ? 'text-amber-700' :
                'text-rose-700';
              return (
                <tr key={m.key} className="border-t border-slate-100" data-testid={`work-completion-row-${m.key}`}>
                  <td className="p-2 px-3 font-semibold">{m.label} <span className="text-[10px] text-slate-400 font-normal">({m.unit})</span></td>
                  <td className="p-2 px-3 text-right font-mono-num text-slate-600" data-testid={`work-completion-est-${m.key}`}>
                    {est > 0 ? est.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}
                  </td>
                  <td className="p-2 px-3">
                    <input
                      type="number" min={0} step={0.1}
                      data-testid={`work-completion-actual-${m.key}`}
                      value={act || ''}
                      onChange={(e) => setActual(m.key, { qty: Number(e.target.value), unit: m.unit })}
                      placeholder="0"
                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono-num text-right"
                    />
                  </td>
                  <td className={`p-2 px-3 text-right font-mono-num text-xs font-bold ${variancePillCls}`} data-testid={`work-completion-variance-${m.key}`}>
                    {v === null ? '—' : (v > 0 ? `+${v.toFixed(1)}%` : `${v.toFixed(1)}%`)}
                  </td>
                  <td className="p-2 px-3">
                    <input
                      type="text"
                      data-testid={`work-completion-notes-${m.key}`}
                      value={(actuals[m.key] || {}).notes || ''}
                      onChange={(e) => setActual(m.key, { notes: e.target.value, unit: m.unit })}
                      placeholder="Reason for variance, supplier, batch…"
                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase font-bold tracking-wide text-slate-500">Completion date</span>
          <input
            type="date"
            data-testid="work-completion-date"
            value={completedAt}
            onChange={(e) => setCompletedAt(e.target.value)}
            className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
          />
        </label>
        <label className="block md:col-span-1">
          <span className="text-[10px] uppercase font-bold tracking-wide text-slate-500">Completion notes</span>
          <textarea
            data-testid="work-completion-notes"
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            rows={3}
            placeholder="Site conditions, anything that affected material usage, items returned to supplier, etc."
            className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
          />
        </label>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 inline-flex items-start gap-2">
        <TrendingUp size={13} className="mt-0.5 flex-shrink-0" />
        <span>
          Saved actuals feed the rolling <strong>estimate-vs-actual ratio</strong> across every completed job. When the rolling ratio drifts more than 5% from 1.0, the system surfaces a SUGGESTED rate-card adjustment in Settings — never auto-applied, the office decides when to accept.
        </span>
      </div>
    </div>
  );
}
